import React, { Component, ContextType, createRef } from 'react';
import ApiUrls from '@api/apiUrls';
import { fetchApi } from '@api/fetchApi';
import { IApiGroupMappingType, IApiSelfTradingAccountTypeViewModel } from '@api/interfaces/data-contracts';
import { WarningMessage } from '@components-library/WarningMessage/WarningMessage';
import { AccountCreateButtons } from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateButtons/AccountCreateButtons';
import { BeforeActionsContent } from '@components-member/TradingAccounts/AccountCreate/components/BeforeActionContent/BeforeActionContent';
import { formProvider } from '@components-member/TradingAccounts/AccountCreate/components/Forms/Trading/formProvider';
import { AccountCreateContext } from '@components-member/TradingAccounts/AccountCreate/context';
import { EnumUTAFormTAField } from '@components-member/TradingAccounts/AccountCreate/enums';
import {
  changeLeverage,
  getAccountType,
  setIbCodeTooltip,
} from '@components-member/TradingAccounts/AccountCreate/helpers';
import { IBaseAccountCreateProps } from '@components-member/TradingAccounts/AccountCreate/interfaces';
import type { IAccountCreationQueryParams } from '@components-member/TradingAccounts/hooks/useAccountCreationQueryParams';
import { MessageDataType, UserLeverageHint } from '@components-member/shared/UserLeverageHint';
import { IModalFormField } from '@components-member/socialTrading/social.interfaces';
import { filterIbCodeFieldIfDisabled } from '@components-member/socialTrading/utils';
import { FormUtils } from '@components-shared/forms/forms.utils';
import Preloader from '@components-shared/preloader';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { getInitialState } from 'components/shared/forms/formGenerator';
import { TRADING_ACCOUNT_FORM_TYPE } from 'global.enums';
import { observer } from 'mobx-react';
import { mobxStore } from 'store/mobx';
import tradingAccountFormStyles from '../tradingAccountForm.styles.module.scss';
import styles from './TradingAccountCreate.styles.module.scss';
import { ITradingAccountCreateState } from './interfaces';

interface ITradingAccountCreateProps extends IBaseAccountCreateProps {
  formFields: IModalFormField[];
}

const GMI_BONUS_TAILAND = 445;

class TradingAccountCreate extends Component<ITradingAccountCreateProps, ITradingAccountCreateState> {
  static contextType = AccountCreateContext;

  // @ts-ignore declare cannot be used at the current moment
  context!: ContextType<typeof AccountCreateContext>;

  FIELDS: IModalFormField[];

  formRef = createRef<{
    update: (data: IModalFormField[]) => void;
    validate: () => boolean;
  }>();

  formComponent: ReturnType<typeof formProvider>;

  tradingAccountsStore = mobxStore.tradingAccountsStore;

  constructor(props: ITradingAccountCreateProps) {
    super(props);

    const { formFields } = props;

    this.FIELDS = [...formFields];

    this.formComponent = formProvider(this.FIELDS);

    this.state = {
      showAdditionalAgreeLink: false,
      processing: true,
      isCreatingTradingAccount: false,
      agree: false,
      form: { ...getInitialState(this.FIELDS) },
      tradingAccountSettings: {
        leverageWarnings: {},
      },
      selectedAccountDescription: null,
    };
  }

  async componentDidMount(): Promise<void> {
    await this.getInitialData();
  }

  setAccountTypeOptionList = (
    accountFormType: string,
    selectedPlatform?: IAccountCreationQueryParams['platformType'],
  ): void => {
    const { selfTradingTypes } = this.context.accountCreateState;
    const idxTypeField = FormUtils.getFieldIndex(this.FIELDS, EnumUTAFormTAField.type);

    if (idxTypeField > -1) {
      const isSelectedPlatformNotSelected = selectedPlatform === 'None' || !selectedPlatform;
      const options = selfTradingTypes
        .filter(
          ({ accountType, tradingPlatformType }) =>
            accountType === accountFormType &&
            (isSelectedPlatformNotSelected || selectedPlatform === tradingPlatformType),
        )
        .map(({ name, displayName, id }) => ({ name: displayName ?? name, value: id }));

      FormUtils.updateFormField(idxTypeField, { options }, this.FIELDS);
    }
  };

  formInitializer = (): void => {
    const { canSetIb, clientSourceType, ibCodePrefix } = this.props;
    const { selectedPlatformType } = this.context;

    setIbCodeTooltip({ fields: this.FIELDS, canSetIb, clientSourceType, ibCodePrefix });
    this.FIELDS = filterIbCodeFieldIfDisabled(this.FIELDS, canSetIb, clientSourceType);
    this.setAccountTypeOptionList(this.props.accountType, selectedPlatformType);

    this.updateForm();
  };

  getInitialData = async (): Promise<void> => {
    try {
      this.setState({ processing: true });

      const tradingAccountSettings = await fetchApi.get<MessageDataType>(ApiUrls.userTradingAccountSettings.get);

      this.setState({ processing: false, tradingAccountSettings: tradingAccountSettings.data }, this.formInitializer);
    } catch (error) {
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    }
  };

  typeTriggerHandler = (value: number): void => {
    const { selfTradingTypes } = this.context.accountCreateState;
    const { form } = this.state;
    const accountType = getAccountType(selfTradingTypes, value);
    const leverageIdx = FormUtils.getFieldIndex(this.FIELDS, EnumUTAFormTAField.leverage);

    this.setState({
      selectedAccountDescription: accountType?.description ?? null,
    });

    if (!accountType || !leverageIdx) {
      return;
    }

    changeLeverage(leverageIdx, accountType, this.FIELDS);

    form.leverage = '';
    this.setState({ form });

    this.hideLeverageFieldForMamInvestorType(leverageIdx, accountType);

    this.updateForm();
  };

  createAccount = async (): Promise<void> => {
    const isPreloaderEnable = true;

    const { refresh } = this.context;

    const isValid = this.isValidForm();

    if (!isValid) {
      return;
    }

    try {
      this.setState({ isCreatingTradingAccount: true });
      await this.tradingAccountsStore.createSelfTradeAccount(this.state.form, this.props.user);
      this.props.closeHandler();
      refresh({ isPreloaderEnable });
    } finally {
      this.setState({ isCreatingTradingAccount: false });
    }
  };

  hideLeverageFieldForMamInvestorType = (
    leverageIdx: number,
    accountType: IApiSelfTradingAccountTypeViewModel,
  ): void => {
    const isMamInvestorAccount = accountType.groupMappingType === IApiGroupMappingType.RealMamInvestor;

    if (!isMamInvestorAccount) {
      return;
    }

    const leverageValue = accountType.leverages[0];

    FormUtils.updateFormField(leverageIdx, { isHidden: true }, this.FIELDS);

    this.onChange(EnumUTAFormTAField.leverage, leverageValue);
  };

  renderLeverageHint = (): JSX.Element | null => {
    const hasLeverageField = FormUtils.getFieldIndex(this.FIELDS, EnumUTAFormTAField.leverage) > -1;

    if (!hasLeverageField) {
      return null;
    }

    const { leverageWarnings } = this.state.tradingAccountSettings;

    if (!Object.keys(leverageWarnings).length) {
      return null;
    }

    return (
      <div className={styles.notificationMessage}>
        <UserLeverageHint message={leverageWarnings} />
      </div>
    );
  };

  renderBeforeActionContent = () => {
    const { agree, selectedAccountDescription, showAdditionalAgreeLink } = this.state;
    const { accountType } = this.props;
    return (
      <BeforeActionsContent
        changeAgree={this.changeAgree}
        agree={agree}
        translatonKey={
          showAdditionalAgreeLink ? 'tradingAccountsOpenAccountAgreeNew' : 'tradingAccountsOpenAccountAgree'
        }
        outerFooterChildren={
          selectedAccountDescription && (
            <WarningMessage
              className={tradingAccountFormStyles.noteMessage}
              title={keyMap.commonPleaseNote}
              type="warning">
              {selectedAccountDescription}
            </WarningMessage>
          )
        }
        innerFooterChildren={accountType === TRADING_ACCOUNT_FORM_TYPE.SELF && this.renderLeverageHint()}
      />
    );
  };

  changeAgree = (): void => {
    const { agree } = this.state;

    this.setState({ agree: !agree });
  };

  updateForm = (): void => {
    if (this.formRef?.current) {
      this.formRef.current.update(this.FIELDS);
    }
  };

  isValidForm = (): boolean | undefined => {
    if (this.formRef && this.formRef.current) {
      return this.formRef.current.validate();
    }
  };

  onChange = (name: string, value: unknown): void => {
    const { form } = this.state;
    const formName = name as keyof typeof form;
    const formValue = value as (typeof form)[keyof typeof form];
    if (formName === 'type') {
      if (formValue === GMI_BONUS_TAILAND) {
        this.setState({ showAdditionalAgreeLink: true });
      } else {
        this.setState({ showAdditionalAgreeLink: false });
      }
    }

    //@ts-ignore
    form[name] = value;
    this.setState({ form });
  };

  render(): JSX.Element {
    const { processing, form, agree, isCreatingTradingAccount } = this.state;
    const { isBackButtonDisplayed, changeStep } = this.props;

    const { isAccountLimitForUnverifiedUsers } = this.context;

    if (processing) {
      return <Preloader />;
    }

    const Form = this.formComponent;

    const disabled = !agree || isAccountLimitForUnverifiedUsers || isCreatingTradingAccount;

    return (
      <Form
        className="grid--form"
        form={form}
        ref={this.formRef}
        onChange={this.onChange}
        typeTrigger={this.typeTriggerHandler}
        beforeActions={this.renderBeforeActionContent()}
        after={
          <AccountCreateButtons
            createAction={this.createAccount}
            disabled={disabled}
            changeStepHandler={changeStep}
            isBackButtonDisplayed={isBackButtonDisplayed}
            isCreatingTradingAccount={isCreatingTradingAccount}
          />
        }
      />
    );
  }
}

const TradingAccountCreateWithObserver = observer(TradingAccountCreate);

export { TradingAccountCreateWithObserver as TradingAccountCreate };
